import { useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { migrateTokensToSanctum, validateSanctumToken } from '../utils/tokenMigration';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Inicializar estado desde localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 useAuth - Inicializando autenticación...');
      setIsLoading(true);
      
      try {
        // Migrar tokens JWT antiguos a Sanctum
        const hadOldTokens = migrateTokensToSanctum();
        
        if (hadOldTokens) {
          console.log('🔄 Tokens migrados, usuario necesita re-login');
          setUser(null);
          setSelectedCompany(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
        
        // Validar token actual
        const currentToken = authService.getToken();
        if (currentToken && !validateSanctumToken(currentToken)) {
          console.log('🚨 Token inválido para Sanctum, limpiando...');
          authService.clearStorage();
          setUser(null);
          setSelectedCompany(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
        
        const storedUser = authService.getUser();
        const storedCompany = authService.getSelectedCompany();
        const isAuth = authService.isAuthenticated();

        console.log('💾 Datos desde localStorage:');
        console.log('👤 Usuario almacenado:', storedUser);
        console.log('🏢 Empresa almacenada:', storedCompany);
        console.log('🔍 isAuthenticated:', isAuth);

        if (isAuth && storedUser) {
          console.log('🔄 Verificando validez del token con el servidor...');
          
          // Verificar que el token sigue siendo válido
          const userResult = await authService.getCurrentUser();
          
          if (userResult.success) {
            console.log('✅ Token válido - Usuario autenticado');
            setUser(userResult.user);
            setSelectedCompany(storedCompany);
            setIsAuthenticated(true);
          } else {
            console.log('❌ Token inválido - Limpiando autenticación');
            // Token inválido, limpiar todo
            authService.clearStorage();
            setUser(null);
            setSelectedCompany(null);
            setIsAuthenticated(false);
          }
        } else {
          console.log('⚠️ No hay datos de autenticación - Usuario no logueado');
          setUser(null);
          setSelectedCompany(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('🚨 ERROR al inicializar autenticación:', error);
        authService.clearStorage();
        setUser(null);
        setSelectedCompany(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
        console.log('🏁 useAuth - Inicialización completada');
      }
    };

    initializeAuth();
  }, []);

  // Función para hacer login
  const login = useCallback(async (email, password) => {
    console.log('🔐 useAuth.login() - Iniciando login');
    console.log('📧 Email:', email);
    
    setIsLoading(true);
    
    try {
      const result = await authService.login(email, password);
      
      console.log('📥 Resultado del login:', result);
      
      if (result.success) {
        console.log('✅ Login exitoso en useAuth');
        console.log('👤 Usuario a establecer:', result.user);
        
        // Actualizar estado de forma inmediata
        setUser(result.user);
        setIsAuthenticated(true);
        setSelectedCompany(null); // Limpiar empresa para forzar selector
        setIsLoading(false); // Importante: poner loading en false inmediatamente
        
        console.log('🔄 Estado actualizado en useAuth - isLoading ahora es false');
      } else {
        console.log('❌ Login falló en useAuth');
        setUser(null);
        setIsAuthenticated(false);
        setSelectedCompany(null);
        setIsLoading(false);
      }
      
      return result;
    } catch (error) {
      console.error('🚨 ERROR en useAuth.login():', error);
      setUser(null);
      setIsAuthenticated(false);
      setSelectedCompany(null);
      setIsLoading(false);
      return {
        success: false,
        message: 'Error de conexión'
      };
    }
  }, []);

  // Función para hacer register
  const register = useCallback(async (userData) => {
    console.log('📝 useAuth.register() - Iniciando registro');
    setIsLoading(true);
    
    try {
      const result = await authService.register(userData);
      
      console.log('📥 Resultado del registro:', result);
      
      if (result.success) {
        console.log('✅ Registro exitoso en useAuth');
        setUser(result.user);
        setIsAuthenticated(true);
        setSelectedCompany(null);
      }
      
      return result;
    } catch (error) {
      console.error('🚨 ERROR en useAuth.register():', error);
      return {
        success: false,
        message: 'Error de conexión'
      };
    } finally {
      setIsLoading(false);
      console.log('🏁 useAuth.register() - Completado');
    }
  }, []);

  // Función para hacer logout
  const logout = useCallback(async () => {
    console.log('🚪 useAuth.logout() - Cerrando sesión');
    setIsLoading(true);
    
    try {
      await authService.logout();
      console.log('✅ Logout exitoso en useAuth');
    } catch (error) {
      console.error('🚨 ERROR en useAuth.logout():', error);
    } finally {
      setUser(null);
      setSelectedCompany(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      console.log('🏁 useAuth.logout() - Completado');
    }
  }, []);

  // Función para seleccionar empresa
  const selectCompany = useCallback((company) => {
    console.log('🏢 useAuth.selectCompany():', company);
    authService.setSelectedCompany(company);
    setSelectedCompany(company);
  }, []);

  // Función para actualizar usuario
  const updateUser = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('⚠️ useAuth.updateUser() - Usuario no autenticado');
      return;
    }

    console.log('🔄 useAuth.updateUser() - Actualizando usuario');

    try {
      const result = await authService.getCurrentUser();
      if (result.success) {
        console.log('✅ Usuario actualizado en useAuth');
        setUser(result.user);
      }
      return result;
    } catch (error) {
      console.error('🚨 ERROR en useAuth.updateUser():', error);
      return { success: false, message: 'Error de conexión' };
    }
  }, [isAuthenticated]);

  // Función para verificar permisos (cuando implementemos roles)
  const hasPermission = useCallback((permission) => {
    const result = user && user.permissions && user.permissions.includes(permission);
    console.log(`🔐 useAuth.hasPermission('${permission}'):`, result);
    return result;
  }, [user]);

  // Función para verificar roles (cuando implementemos roles)
  const hasRole = useCallback((role) => {
    const result = user && user.roles && user.roles.some(userRole => userRole.name === role);
    console.log(`👤 useAuth.hasRole('${role}'):`, result);
    return result;
  }, [user]);

  // Log del estado actual cada vez que cambie
  useEffect(() => {
    console.log('📊 useAuth - Estado actual actualizado:');
    console.log('👤 Usuario:', user ? `${user.name} (${user.email})` : 'No autenticado');
    console.log('🏢 Empresa:', selectedCompany ? selectedCompany.name : 'No seleccionada');
    console.log('🔍 isAuthenticated:', isAuthenticated);
    console.log('⏳ isLoading:', isLoading);
    console.log('-------------------');
  }, [user, selectedCompany, isAuthenticated, isLoading]);

  return {
    // Estado
    user,
    selectedCompany,
    isLoading,
    isAuthenticated,
    
    // Acciones
    login,
    register,
    logout,
    selectCompany,
    updateUser,
    
    // Utilidades
    hasPermission,
    hasRole,
    
    // Getters
    getToken: authService.getToken.bind(authService),
    clearStorage: authService.clearStorage.bind(authService)
  };
}; 